import React, { Fragment } from 'react';
import { RadioGroup } from '@headlessui/react';
import clx from 'classnames';

import ReviewFilterType from '../../../filterFactory/ReviewFilter';
import { useTreatmentFilterStateContext, useTreatmentFilterUpdaterContext } from '../../context/TreatmentFilterContext';
import useCachedStatistics from '../hooks/useCacheStatistics';

import FilterSection, { FilterHeadings } from '../FilterUi/FilterSection/FilterSection';
import StarRating from '../../../../../_ui/_blocks/StarRating/StarRating';
import Typography from '../../../../../_ui/_blocks/Typography/Typography';
import FilterLabel from '../FilterUi/FilterLabel/FilterLabel';
import RadioIcon from '../../../../../_ui/_blocks/Form/Inputs/Radio/RadioIcon/RadioIcon';

const ratings = {
    Five: 5,
    Four: 4,
    Three: 3,
    Two: 2,
    One: 1,
};

type RatingKeys = keyof typeof ratings;

const FILTER_KEY = 'review';

const ReviewFilter = () => {
    const { filters } = useTreatmentFilterStateContext();
    const { updateFilters, setLastFilterUpdated } = useTreatmentFilterUpdaterContext();
    const statistics = useCachedStatistics(FILTER_KEY);

    const reviewFilter = filters.get(FILTER_KEY) as ReviewFilterType;

    const handleChange = (value: unknown) => {
        reviewFilter.keys.clear();
        if (value) {
            reviewFilter.keys.add(value as number);
        }
        updateFilters();
        setLastFilterUpdated(FILTER_KEY);
    };

    return (
        <FilterSection title={FilterHeadings.review} className="space-y-100">
            <RadioGroup value={reviewFilter.currentRating()} onChange={handleChange} as="ul" className="space-y-125">
                <RadioGroup.Option key="no-rating" value={0} as="li" className="flex items-center justify-between cursor-pointer">
                    {({ checked }) => (
                        <Fragment>
                            <RadioGroup.Label className="flex items-center gap-050 cursor-pointer">
                                <FilterLabel>Show all ratings</FilterLabel>
                            </RadioGroup.Label>
                            <RadioIcon checked={checked} />
                        </Fragment>
                    )}
                </RadioGroup.Option>
                {(Object.keys(ratings) as RatingKeys[]).map((rating) => {
                    const ratingValue = ratings[rating];
                    const statistic = statistics.find((item) => item.key === ratingValue)?.statistics.value || 0;

                    const disabled = statistic === 0;
                    const className = clx('flex items-center justify-between cursor-pointer', {
                        'opacity-50': disabled,
                    });

                    return (
                        <RadioGroup.Option key={rating} value={ratingValue} as="li" className={className}>
                            {({ checked }) => (
                                <Fragment>
                                    <RadioGroup.Label className="space-y-025 cursor-pointer">
                                        <StarRating rating={ratingValue} size="medium" className={disabled ? 'grayscale' : undefined} />
                                        <span className="flex items-center gap-050">
                                            <Typography typeset="subheading" size="070">
                                                {rating} star{rating === 'One' ? '' : 's'} {rating === 'Five' ? 'only' : 'and over'}
                                            </Typography>
                                            <Typography typeset="subheading" size="070" color="quiet">
                                                ({statistic})
                                            </Typography>
                                        </span>
                                    </RadioGroup.Label>
                                    <RadioIcon checked={checked} />
                                </Fragment>
                            )}
                        </RadioGroup.Option>
                    );
                })}
            </RadioGroup>
        </FilterSection>
    );
};

export default ReviewFilter;
